import { Dispatch, SetStateAction, useCallback } from 'react';
import Text from 'common/ui/Text';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Close from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { Stack, Spacer } from 'common/ui/Layout';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { SettingsCard, SettingsCardContent, SettingsCardHeader } from './SettingsLayoutComponents';
import {
  DEFAULT_SHOW_PICKLIST_OPTIONS_IN_EDITOR,
  DEFAULT_AUTO_FILL_COMPARISON_ENABLED,
} from 'config/constants';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import type {
  UpdateDictaphonePreferencesMutation,
  UpdateDictaphonePreferencesMutationVariables,
} from 'generated/graphql';
import {
  GET_TOOL_PREFERENCES,
  UPDATE_DICTAPHONE_PREFERENCES,
  GET_ME,
  UPDATE_REPORTER_SETTINGS,
} from 'modules/Apollo/queries';
import { useToolPreferences } from 'hooks/usePreferences/index';
import { MenuSeparator } from '../ui/MenuSeparator/index';
import { useFocusMode, FocusModeToggleMethod } from 'hooks/useFocusMode';
import { telemetryContextFor } from 'modules/Apollo/types';
import { ReporterImpressionGenerationSettings } from './ReporterImpressionGenerationSettings';
import { ReporterWorkstationIDSettings } from './ReporterWorkstationIDSettings';

type ReporterSettingsTabProps = {
  onClose: () => void;
  setError: Dispatch<SetStateAction<string | null>>;
};

export const ReporterSettingsTab = ({
  onClose,
  setError,
}: ReporterSettingsTabProps): React.ReactElement => {
  const [updateReporterSettings] = useMutation(UPDATE_REPORTER_SETTINGS, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const me = data?.me;
  const reporterSettings = me?.reporterSettings;
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const { isFocusModeSettingEnabled, setIsFocusModeSettingEnabled, toggleFocusMode } = useFocusMode(
    { context: APOLLO_QUERY_CONTEXT }
  );

  const [updateDictaphonePreferences] = useMutation<
    UpdateDictaphonePreferencesMutation,
    UpdateDictaphonePreferencesMutationVariables
  >(UPDATE_DICTAPHONE_PREFERENCES, {
    context: APOLLO_QUERY_CONTEXT,
  });

  const [toolPreferences] = useToolPreferences({ context: APOLLO_QUERY_CONTEXT });
  const [isReporterWorkstationIDEnabled] = useFeatureFlagEnabled(FF.REPORTER_WORKSTATION_ID);

  const showPicklistOptionsInEditor =
    toolPreferences?.dictaphone.showPicklistOptionsInEditor ??
    DEFAULT_SHOW_PICKLIST_OPTIONS_IN_EDITOR;
  const mergeFieldsSettings = reporterSettings?.mergeFieldsSettings;

  const isAutoFillComparisonEnabled =
    reporterSettings?.autoFillComparison?.enabled ?? DEFAULT_AUTO_FILL_COMPARISON_ENABLED;

  const toggleShowPicklistOptionsInEditor = useCallback(async () => {
    if (toolPreferences == null) return;

    const updatedResponse = {
      ...toolPreferences.dictaphone,
      showPicklistOptionsInEditor: !showPicklistOptionsInEditor,
    } as const;

    await updateDictaphonePreferences({
      variables: { showPicklistOptionsInEditor: !showPicklistOptionsInEditor },
      optimisticResponse: {
        __typename: 'Mutation',
        updateDictaphonePreferences: {
          __typename: 'DictaphonePreferences',
          ...updatedResponse,
        },
      },
      update: (proxy) => {
        proxy.writeQuery({
          query: GET_TOOL_PREFERENCES,
          data: {
            toolPreferences: {
              ...toolPreferences,
              dictaphone: updatedResponse,
            },
          },
        });
      },
    });
  }, [showPicklistOptionsInEditor, toolPreferences, updateDictaphonePreferences]);

  const handleSaveSettings = useCallback(
    async ({
      triggerWord = reporterSettings?.triggerWord,
      styles = reporterSettings?.styles,
      cursorStyle = reporterSettings?.cursorStyle,
      textSelectionBehavior = reporterSettings?.textSelectionBehavior,
      lineIndicator = reporterSettings?.lineIndicator,
      mergeFieldsSettings = reporterSettings?.mergeFieldsSettings,
      impressionGenerator = reporterSettings?.impressionGenerator,
      autoFillComparison = reporterSettings?.autoFillComparison,
      focusMode = reporterSettings?.focusMode,
      audioQualityWarnings = reporterSettings?.audioQualityWarnings,
    }) => {
      await updateReporterSettings({
        optimisticResponse: {
          __typename: 'Mutation',
          updateReporterSettings: {
            __typename: 'ReporterSettings',
            triggerWord,
            styles,
            cursorStyle,
            textSelectionBehavior,
            lineIndicator,
            mergeFieldsSettings,
            impressionGenerator,
            autoFillComparison,
            focusMode,
            audioQualityWarnings,
          },
        },
        update: (proxy) => {
          proxy.writeQuery({
            query: GET_ME,
            data: {
              me: {
                ...me,
                reporterSettings: {
                  __typename: 'ReporterSettings',
                  triggerWord,
                  styles,
                  cursorStyle,
                  textSelectionBehavior,
                  lineIndicator,
                  mergeFieldsSettings,
                  impressionGenerator,
                  autoFillComparison,
                  focusMode,
                  audioQualityWarnings,
                },
              },
            },
          });
        },
        variables: {
          triggerWord,
          styles,
          cursorStyle,
          textSelectionBehavior,
          lineIndicator,
          mergeFieldsSettings,
          impressionGenerator,
          autoFillComparison,
          focusMode,
          audioQualityWarnings,
        },
      });
    },
    [
      me,
      reporterSettings?.cursorStyle,
      reporterSettings?.textSelectionBehavior,
      reporterSettings?.impressionGenerator,
      reporterSettings?.lineIndicator,
      reporterSettings?.mergeFieldsSettings,
      reporterSettings?.styles,
      reporterSettings?.triggerWord,
      reporterSettings?.autoFillComparison,
      reporterSettings?.focusMode,
      reporterSettings?.audioQualityWarnings,
      updateReporterSettings,
    ]
  );

  return (
    <SettingsCard>
      <SettingsCardHeader>
        <Stack alignY="center">
          <Text variant="display2">Reporter Settings</Text>
          <Spacer />
          <IconButton onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Stack>
      </SettingsCardHeader>
      <SettingsCardContent>
        <Stack vertical space="small">
          <Stack vertical space="xxsmall">
            <Text variant="display1" display="block">
              General
            </Text>
            <Stack alignY="center">
              <Text>Show picklist options in the Editor</Text>
              <Spacer />
              <Switch
                data-testid="picklist-editor-switch"
                checked={showPicklistOptionsInEditor}
                onChange={toggleShowPicklistOptionsInEditor}
              />
            </Stack>
            <Stack alignY="center">
              <Text>Automatically populate Comparison field when prior studies are reviewed</Text>
              <Spacer />
              <Switch
                data-testid="auto-fill-comparison-switch"
                checked={isAutoFillComparisonEnabled}
                onChange={(event) => {
                  handleSaveSettings({
                    autoFillComparison: {
                      ...reporterSettings?.autoFillComparison,
                      enabled: event.target.checked,
                    },
                  });
                }}
              />
            </Stack>
          </Stack>
          <MenuSeparator />

          <Stack vertical space="xsmall">
            <Text variant="display1" display="block">
              Navigation
            </Text>
            <Stack vertical>
              <Stack alignY="center">
                <Text>Include merge fields in tab navigation</Text>
                <Spacer />
                <Switch
                  data-testid="merge-field-navigation-switch"
                  checked={!mergeFieldsSettings?.ignoreNavigation}
                  onChange={(event) => {
                    handleSaveSettings({
                      mergeFieldsSettings: {
                        __typename: 'MergeFieldsSettings',
                        ignoreNavigation: !event.target.checked,
                        ignoreDefaultSelection:
                          reporterSettings?.mergeFieldsSettings?.ignoreDefaultSelection ?? false,
                      },
                    });
                  }}
                />
              </Stack>
              <Stack alignY="center">
                <Text>Ignore merge fields while identifying first input field in report</Text>
                <Spacer />
                <Switch
                  data-testid="merge-field-default-selection-switch"
                  onChange={() => {
                    handleSaveSettings({
                      mergeFieldsSettings: {
                        __typename: 'MergeFieldsSettings',
                        ignoreNavigation: mergeFieldsSettings?.ignoreNavigation ?? false,
                        ignoreDefaultSelection: !mergeFieldsSettings?.ignoreDefaultSelection,
                      },
                    });
                  }}
                  checked={mergeFieldsSettings?.ignoreDefaultSelection}
                />
              </Stack>
            </Stack>
          </Stack>
          {isReporterWorkstationIDEnabled && (
            <>
              <MenuSeparator />
              <ReporterWorkstationIDSettings />
            </>
          )}
          <MenuSeparator />
          {isAutoImpressionsEnabled && (
            <ReporterImpressionGenerationSettings
              reporterSettings={reporterSettings}
              handleSaveSettings={handleSaveSettings}
            />
          )}
          <>
            <MenuSeparator />
            <Stack vertical space="xsmall">
              <Text variant="display1" display="block">
                Focus Mode
              </Text>
              <Stack alignY="center">
                <Text>Show Focus Mode toggle in Reporter</Text>
                <Spacer />
                <Switch
                  data-testid="focus-mode-switch"
                  checked={isFocusModeSettingEnabled}
                  onChange={(event) => {
                    const { checked } = event.target;
                    handleSaveSettings({
                      focusMode: {
                        ...reporterSettings?.focusMode,
                        enabled: checked,
                      },
                    });
                    // to sync the visibility of the Focus Mode toggle with the Reporter window
                    setIsFocusModeSettingEnabled(checked);
                    if (checked === false) {
                      toggleFocusMode({
                        method: FocusModeToggleMethod.FormControl,
                        enabled: false,
                      });
                    }
                  }}
                />
              </Stack>
            </Stack>
          </>
        </Stack>
      </SettingsCardContent>
    </SettingsCard>
  );
};

ReporterSettingsTab.displayName = 'ReporterSettingsTab';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ReporterSettingsTab);
